import { polyquantMarket } from '../lib/polyquantLabels';
import { leagueTitle } from '../lib/sports';
import { PRIVATE_MIRROR, RU_FEED } from '../lib/feedContext';
import { CupisWorkTools } from './CupisWorkTools';
import { Decimal } from 'decimal.js';
import { ZeroHint } from './ZeroGuide';
import { zeroPair } from '../lib/zero';
import { BookOpenStatus } from './BookOpenStatus';
/**
 * Раскрытая строка — витрина внутри ленты: один экран, две глубины.
 *
 * Три вещи, ради которых она существует.
 *
 * Порядок ног. Биржевая ставится первой не по традиции: она исполняется
 * лимиткой и может не налиться, а контора принимает мгновенно. Поставив
 * сначала в контору, человек рискует остаться с одной ногой на всю сумму —
 * это не арбитраж, это ставка. Поэтому вторая кнопка заблокирована, пока
 * первая не подтверждена. Когда биржевой ноги нет вовсе, причину порядка мы
 * не называем: между двумя конторами он держится порядком выдачи, и объяснять
 * его словами значило бы выдать случайность за расчёт.
 *
 * Замер темпа. Подтверждения ног — не отметки для отчёта, а единственный
 * честный способ узнать, сколько человек тратит. Спросить нельзя: ответ «за
 * пять секунд» дают в спокойный момент, а врёт он ровно тогда, когда ошибка
 * дороже всего.
 *
 * Процент. Он живёт здесь и только здесь — рядом с глубиной стакана, где виден
 * его настоящий вес. 27 % на стакане в шесть долларов — это двести рублей, и
 * поставить их рядом важнее, чем показать процент крупно.
 */

import { useDeferredValue, useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react';

import { placeMark } from '../lib/align';
import { forkCheck } from '../confirm/forkCheck';
import { snapPages } from '../confirm/snap';
import { rememberSite } from './liveQuote';
import type { CoverQuote } from '../lib/cover';
import { notePages } from '../lib/sight';
import type { Fork } from '../lib/protocol';
import { cardKind, isCodeName } from './matchTitle';
import { PredictFacts } from './PredictFacts';
import { CaptureButton } from '../book/cash/CaptureButton';
import { rememberHref } from './pickHref';
import { ReviewShare } from './ReviewShare';
import { rescue as rescueOf } from '../lib/rescue';
import { HAND_SOURCE, centsText, type RowLeg } from '../lib/row';
import { moneyLie } from './moneyLie';
import { rowWithFork, rowWithLock } from './rows';
import { liveHoldKey, paintLiveAsks } from './liveBook';
import { tempoOf, useSettings } from '../store/settings';
import type { Pressure, Standing } from './accounts';
import { pressureOf, pressureText, useAccounts } from './accounts';

import { Brand } from './Brand';

import { CoverDock, CoverStrip, CoverSum, CoverTile, oppositeLeg } from './CoverPane';
import * as act from './act';
import { useDesk } from './desk';
import { useEntry } from './entry';
import { buyText, useExchange } from './exchange';
import { Clip, Grab, copy } from './Grab';
import { InitiatorDot } from './InitiatorDot';
import { PAPER_UI, settle, usePaper } from './paper';
import { olimpEntryNote } from './olimpDoor';
import { opponentOf } from './positiveDoor';
import { ExitPane, type Kind as ExitKind } from './ExitPane';
import { marketNote, rareNote, voidNote } from './legNotes';
import { betFace, betOn, matchTitleInOrder } from './pick';
import { rub, usd } from './format';
import {
  cardMoney,
  cardSay,
  catalogCur,
  convertStakeForm,
  editAmount,
  forForm as formOf,
  formAmount,
  hasRubBook,
  isRubStake,
  lockAsOf,
  moneyAmount,
  profitText,
  stakeLockBody,
  useShow,
  venueMoney,
} from './show';
import type { Meta } from './meta';
import { venueName } from './meta';
import { nakedOf, nakedText } from './naked';
import { holdLegs, isPolymarket, leadWhy, legOrder, legsFirstOf, legSeat } from './order';
import { useProfile } from './profile';
import { useFill } from './polyFill';
import { leaveSeed } from './forkSeed';
import { popOut } from './pop';
import type { FeedRow } from './rows';
import { Sign } from './Sign';
import { type Stillness, shareStill, shareStillSay, stillOf, stillness, stillText } from './still';
import { whenText } from './when';
import { AmountEdit } from './AmountEdit';
import { Calc } from './Calc';
import { pctSaid, snapOf, shiftSay } from './shiftSay';
import { useClips } from './clip';

import { CurEdit } from './CurEdit';
import { ShareUnitEdit } from './ShareUnitEdit';
import { useSession } from '../access/session';
import { polyMarketAllowed } from '../poly/gate';
import { PolyBookLadder } from './PolyBookLadder';
import { polyMarketCheck, PolyOrderEdit } from './PolyMarket';
import { useLivePolyBook } from './useLivePolyBook';
import { FeeEdit } from './FeeEdit';
import { FreeEdit } from './FreeEdit';
import { OddsEdit } from './OddsEdit';
import { blankStep, useScreen } from './store';
export { popOut };

/**
 * Что сказать про счёт в этой конторе перед нажатием. `null` — говорить нечего.
 *
 * Порезанный счёт называем всегда, перекос — только когда он посчитан на
 * достаточном числе ставок. Оба сообщения без совета: мы не знаем ни его
 * остальных счетов, ни того, сколько он собирается ставить сегодня.
 */
export function accountNote(standing: Standing | undefined, pressure: Pressure): string | null {
  if (standing === 'limited') {
    return pressure.crowded
      ? `счёт порезан, и ${pressureText(pressure)}`
      : 'счёт порезан, проверьте, сколько контора примет';
  }
  if (standing === 'closed') return 'счёт закрыт';
  return pressure.crowded ? pressureText(pressure) : null;
}

/** Сколько секунд зелёное «жива» ещё про сейчас, а не про прошлое нажатие. */
export const CHECK_FRESH_MS = 60_000;

function lagText(seconds: number, stale: boolean): string {
  const age = seconds < 10 ? seconds.toFixed(1) : String(Math.round(seconds));
  return stale ? `молчит ${age} с` : `${age} с назад`;
}

/** Возраст цены, не пульса площадки. Тишина хуже падения. */
export function sourceLag(meta: Meta, code: string): string | null {
  const one = meta.sources.find((row) => row.code === code);
  if (one === undefined) return null;
  // origin — последняя НОВАЯ цена. age — любой кадр, в том числе переиздание
  // той же 55¢. Путать их нельзя: карточка Belkovics писала «0.8 с назад»
  // при цене, которой на сайте уже не было.
  const n = one.originAgeSeconds ?? one.ageSeconds;
  if (n === null) return null;
  return lagText(n, one.stale);
}

/** На плитке: сколько стоит ЭТА цена. Пульс площадки — запасной. */
export function tileLag(
  fork: Record<string, unknown>,
  source: string,
  meta: Meta,
  now: number,
): string | null {
  const stood = stillOf(fork, source, now);
  if (stood !== null && stood.seconds >= 5) {
    return `${stood.seconds < 10 ? stood.seconds.toFixed(1) : Math.round(stood.seconds)} с без движения`;
  }
  return sourceLag(meta, source);
}

/**
 * Пометка приходит одной фразой по схеме «доказательство — вывод»: «прыжок
 * 2.816→7.800 (2.77×) на 1XBet при ROI 35.51% без согласия рынка — свежая
 * опечатка, не новость». Разрезаем её по тире.
 *
 * Так блок причин перестаёт быть машинным логом. Вывод человек читает, и трёх
 * выводов подряд хватает, чтобы убрать руку с кнопки; доказательство он сверяет
 * глазами, и только если решил спорить. Прежде обе половины стояли одним
 * кеглем в три строки красного текста — единственное место карточки, которое
 * обязано остановить, читалось хуже всего остального.
 *
 * Фраза без тире (наши собственные причины, подписи непроверок) уходит в вывод
 * целиком: додумать за неё доказательство мы не можем.
 */
export function verdict(text: string): { say: string; why: string | null } {
  const cut = text.indexOf(' — ');
  if (cut < 0) return { say: text, why: null };
  return { say: text.slice(cut + 3), why: text.slice(0, cut) };
}

function PickSaid({ text }: { text: string }): ReactElement {
  const face = betFace(text);
  if (face.lead === null) return <>{face.name}</>;
  return (
    <>
      <span className="nx-det__map">{face.lead}</span>
      <span className="nx-det__dash"> — </span>
      <span className="nx-det__who">{face.name}</span>
    </>
  );
}

/**
 * Перевод кодов гейта на язык человека, который сейчас решает «ставить или
 * нет». Серверные фразы точные, но написаны для оператора: «прыжок 1.540→3.750
 * (2.44×) без согласия рынка — свежая опечатка, не новость» требует знать, что
 * такое согласие рынка и почему опечатка хуже новости (08.08, владелец —
 * «ничего не понятно»). Здесь — вывод и его последствие для кошелька; серверная
 * фраза с числами остаётся в карточке доказательством, мелким серым.
 */
const TOO_SHORT_SAY = 'вилка может не дожить';

const RULE_WORDS: Record<string, string> = {
  expired: 'вилка слишком долго висит',
  leg_dead: 'кэф в одной конторе замер',
  slow_book: 'контора опаздывает с кэфом',
  stale_dead: 'этот кэф уже старый',
  no_consensus: 'другие конторы такой кэф не дают',
  crowd_outlier: 'такой кэф больше никто не даёт',
  suspended: 'контора ставки не принимает',
  one_sided: 'на бирже нечем закрыть ставку',
  odds_anomaly: 'один из двух кэфов неверный',
  odds_mismatch: 'один из двух кэфов неверный',
  palp: 'кэф скакнул — похоже на опечатку конторы',
  impossible_roi: 'такого процента на живом рынке не бывает',
  flutter: 'кэф дёргается',
  prism_clone: 'эти конторы берут кэф из одного места',
  unwitnessed_kin: 'обе конторы смотрят в одно место',
  lag_hold: 'одна полка, вторая контора отстаёт на секунду',
  off_set: 'эта контора вне рабочего набора: ставить туда нельзя',
  ot_unverified: 'уточните, считает ли контора овертайм',
  unit_unverified: 'уточните, какую карту считает контора',
  rules_mismatch: 'у контор разные правила',
  frozen: 'кэф давно не меняется',
  statue: 'кэф стоит слишком долго — вилки уже нет',
  draw_possible: 'ничья не закрыта, это не вилка',
  stale: 'кэф уже старый, контора его сменила',
  too_short: TOO_SHORT_SAY,
};

/**
 * «Не успеете вторую ногу» — словами и с числами.
 *
 * Прежняя фраза «такие вилки обычно уходят быстрее, чем человек ставит обе
 * ноги» была правдой, но владелец 08.08: «совершенно непонятно, что он хочет
 * донести?». Непонятно ровно потому, что в ней нет ни числа, ни последствия:
 * быстрее ЧЕГО, насколько и чем это кончится. Число берём измеренное — медиану
 * жизни этой пары площадок, а при пустой выборке медиану похожих пар, и разницу
 * называем словом «похожие»: подставлять групповое число вместо своего молча
 * значит показать знание о соседях как знание об этой вилке. Последствие
 * («останетесь с обычной ставкой на весь банк») дописывает сам блок: оно одно и
 * то же при любых числах, а здесь остаётся только измеренное.
 */
function shortLifeSay(fork: Fork, tempo: number | null): string {
  const own = typeof fork.sv === 'number' ? fork.sv : null;
  const life = own ?? (typeof fork.svGroup === 'number' ? fork.svGroup : null);
  if (life === null) return 'вилка может исчезнуть, пока ставите вторую ставку';
  const whose = own === null ? 'похожие вилки живут' : 'такие вилки живут';
  return tempo === null
    ? `${whose} около ${life} с`
    : `${whose} около ${life} с, две ставки у вас занимают около ${tempo} с`;
}


/**
 * Говорим только когда входа нет вовсе: у донора адреса этой ноги не было, и
 * контору с матчем человек ищет руками. Это меняет его действие, поэтому строка
 * остаётся.
 *
 * Три прежние подписи — «откроется форма ставки», «откроется событие», «откроется
 * раздел» — сняты 11.08 по требованию владельца. Точный адрес есть у меньшинства
 * ног, поэтому одна из этих фраз стояла почти под каждой кнопкой, то есть
 * сообщала не о риске, а о самом факте нажатия. Строка, которая есть всегда,
 * читается один раз в жизни, а место в карточке занимает каждый раз.
 */
function entryNote(leg: RowLeg, eid?: string | null): string | null {
  if (leg.source === HAND_SOURCE) return null;
  if (leg.source === 'csgopositive') {
    // Страницы матча нет, но круг замкнут: клик открывает главную,
    // расширение само ищет строку по двум именам. «Ищи руками» — ложь.
    return null;
  }
  if (leg.source === 'olimp') {
    return olimpEntryNote(
      leg.precision,
      leg.url,
      leg.name,
      opponentOf(leg.name, leg.who),
      eid,
    );
  }
  return leg.url === null ? 'ссылки нет, контору и матч придётся искать руками' : null;
}

/**
 * Первая нога стоит, вторая ещё нет — и цена второй УШЛА. Самый дорогой момент
 * работы: деньги уже отданы, а вилка изменилась под руками. Цена живёт в ленте
 * и меняется дельтами, поэтому числа пересчитываются сами — считать в голове
 * под давлением не приходится.
 *
 * Пока цена вторая стоит на месте, карточки нет вовсе (07.08, владелец):
 * человеку, у которого всё идёт по плану, она пересказывала кнопку «шаг 2»
 * другими словами — пять строк цифр читались как чих, а не как помощь. Блок
 * заслуживает внимания только когда план сломался, и появляться он должен
 * только тогда.
 */
function Rescue({
  row,
  meta,
  vow,
}: {
  row: FeedRow;
  meta: Meta;
  /** Прибыль, обещанная строкой в момент первой ноги. `null` — отметки не было. */
  vow: number | null;
}): React.ReactElement | null {
  const bankFx = useSettings((s) => s.fx);
  const seats = useScreen((s) => s.seatLock[row.id]);
  const legsFirst = legsFirstOf(useProfile((s) => s.profile?.legsFirst));
  const [first, second] = holdLegs(row.row.legs, seats, legsFirst);
  if (first === undefined || second === undefined) return null;
  // Трёхногую позицию одним «добить X» не закрыть: план на две ноги на ней —
  // враньё. Молчание честнее неверного плана.
  if (row.row.legs.length !== 2) return null;
  // Ногу «против» спасение считать не умеет: риск и выигрыш у неё устроены
  // наоборот, и число по обычной формуле было бы враньём ровно в тот момент,
  // когда деньги уже стоят на первой ноге.
  if (first.lay || second.lay) return null;
  if (zeroPair(row.row).reason) return null;
  let plan;
  try {
    plan = rescueOf(first.rub, new Decimal(first.payout).div(first.rub), new Decimal(second.payout).div(second.rub));
  } catch {
    // Негодные числа расчёт не глотает, а бросает. Показать «0 ₽» на живой
    // позиции было бы хуже молчания: по такому нулю приняли бы решение.
    return null;
  }

  // План сломался = закрыться сейчас заметно хуже, чем строка ОБЕЩАЛА в момент
  // первой ноги, либо вилка из ленты ушла. Сверять с текущей прибылью нельзя:
  // она пересчитана из тех же подешевевших цен и совпадает с планом всегда.
  // Допуск в пять процентов (не меньше 20 ₽) съедает дрожь округления: без
  // него блок мигал бы на каждой дельте.
  const promised = vow ?? row.row.profit;
  const drifted = plan.result.toNumber() < promised - Math.max(20, promised * 0.05);
  if (row.ghost === null && !drifted) return null;

  // Биржевую ногу добивают шарами, а не рублями. Курс берётся из самой ноги:
  // раскладка уже перевела её в рубли по нашему курсу, и делить надо тем же
  // числом, иначе спасение разойдётся со строкой на комиссию конвертации.
  const pairFx = second.usd !== null && second.usd > 0 ? second.rub / second.usd : null;
  const shares =
    second.unit === 'share' && pairFx !== null && second.cents
      ? plan.stake.toNumber() / pairFx / (second.cents / 100)
      : null;
  const withRub = hasRubBook(row.row.legs, meta);
  const close = venueMoney(plan.stake.toNumber(), second, bankFx, meta);
  const left = cardMoney(plan.result.toNumber(), bankFx, withRub);
  const openWin = cardMoney(plan.openWin.toNumber(), bankFx, withRub);
  const openLoss = cardMoney(plan.openLoss.toNumber(), bankFx, withRub);
  const firstStake = venueMoney(first.rub, first, bankFx, meta);
  // Тот же процент от вложенного, которым меряется целая вилка: по нему видно,
  // во что превратилась строка, и его же просили с ленты (тестер, 04.09).
  // Рубли без процента не с чем сравнить — вилки бывают на любой банк.
  const leftPct = plan.total.isZero()
    ? null
    : pctSaid(plan.result.div(plan.total).times(100).toNumber());

  return (
    <div className={`nx-det__save ${plan.profitable ? '' : 'lost'}`}>
      <u>
        {row.ghost !== null
          ? 'первая нога стоит, а вилка ушла из ленты. что делать'
          : 'первая нога стоит, а цена второй изменилась. что делать'}
      </u>
      <span>
        закрыть сейчас: поставить <b>{close}</b>
        {shares !== null && ` (${shares.toFixed(1)} шер по ${centsText(second.cents ?? 0)} ¢)`} в{' '}
        {venueName(meta, second.source)} по кэфу <b>{second.odds}</b>, тогда останетесь при{' '}
        <b>
          {plan.profitable ? '+' : ''}
          {cardSay(left)}
        </b>
        {leftPct !== null && ` (${leftPct} от вложенного)`} по расчёту
        . расчёт предполагает приём второй ставки по указанной цене и на всю сумму
      </span>
      {/* Не закрываться — тоже решение, и его цифры человек должен видеть
          рядом. Оставить только «закройтесь» значит принять решение за него. */}
      <span>
        не закрывать: если первая зайдёт, будет <b>+{cardSay(openWin)}</b>, если
        не зайдёт,{' '}
        <b>{cardSay(openLoss)}</b>
      </span>
      {/* Расчёт идёт от суммы и кэфа, которые показаны на шаге 1. Поставил
          человек иначе — числа другие, и узнать об этом он должен здесь. */}
      <span className="nx-det__save-open">
        расчёт от первой ставки {firstStake} по кэфу {first.odds} · пороги выхода в ноль:
      </span>
      <ZeroHint row={row.row} meta={meta} stale={row.ghost !== null} />
    </div>
  );
}

export function Detail({
  row,
  meta,
  alone = false,
  calcBar,
  callActions,
}: {
  row: FeedRow;
  meta: Meta;
  /** Карточка уже и есть отдельное окно: кнопкам «отдельным окном» и «×» тут места нет. */
  alone?: boolean;
  /** Узкая колонка — полоска. Широкая страница колла рисует стол сама. */
  calcBar?: boolean;
  /** Overrides used by a saved Telegram call page. Normal feed keeps its own actions. */
  callActions?: {
    openLeg?: (index: number) => void;
    openBoth?: () => void;
    verify?: () => void;
    status?: string | null;
    /** The saved plan and its display must use the same conversion rate. */
    fx?: number;
  };
}): React.ReactElement {
  const saved = useScreen((s) => s.steps[row.id]);
  const steps = saved ?? blankStep();
  const failLeg = useScreen((s) => s.failLeg);
  const hide = useScreen((s) => s.hide);
  const unhide = useScreen((s) => s.unhide);
  const shelf = useScreen((s) => s.shelf);
  const liveStill = useScreen((s) => s.forks.has(row.id));
  const stakeLock = useScreen((s) => s.stakeLock);
  const setStakeLock = useScreen((s) => s.setStakeLock);
  const oddsLock = useScreen((s) => s.oddsLock);
  const setOddsQuote = useScreen((s) => s.setOddsQuote);
  const feeLock = useScreen((s) => s.feeLock);
  const setFeeQuote = useScreen((s) => s.setFeeQuote);
  const curLock = useScreen((s) => s.curLock);
  const setCurQuote = useScreen((s) => s.setCurQuote);
  const freeLock = useScreen((s) => s.freeLock);
  const setFreeLeg = useScreen((s) => s.setFreeLeg);
  const handLock = useScreen((s) => s.handLock);
  const setHandOdds = useScreen((s) => s.setHandOdds);
  const clips = useClips();
  const pinned = clips.includes(row.id);
  const attempts = useSettings((s) => s.attempts);
  const pair = useSettings((s) => s.pairOpen);
  const bank = useSettings((s) => s.bank);
  const settingsFx = useSettings((s) => s.fx);
  const polyWallet = useSettings((s) => s.polyWallet);
  const polyOrder = useSettings((s) => s.polyOrder);
  const setPolyOrder = useSettings((s) => s.setPolyOrder);
  const polyUser = useSession((s) => s.me.username);
  const polyquant = row.id.startsWith('pq:') || row.id.startsWith('f7:');
  const pilotUser = true;
  const marketPilot = polyMarketAllowed({ username: polyUser });
  const fx = callActions?.fx ?? settingsFx;
  const caps = useSettings((s) => s.caps);
  const capsCur = useSettings((s) => s.capsCur);
  const couponCaps = useSettings((s) => s.couponCaps);
  const freebets = useSettings((s) => s.freebets);
  const freebetsCur = useSettings((s) => s.freebetsCur);
  const roundStep = useSettings((s) => s.roundStep);
  const roundDir = useSettings((s) => s.roundDir);
  const roundWho = useSettings((s) => s.roundWho);
  const exchangeStake = useSettings((s) => s.exchangeStake);
  const setExchangeStake = useSettings((s) => s.setExchangeStake);
  const polyShareTape = row.row.legs.find((leg) => isPolymarket(leg.source) && leg.unit === 'share') ?? null;
  const bookCard = !polyquant && marketPilot && polyShareTape !== null;
  const liveBook = useLivePolyBook(polyShareTape);
  const lockInput = {
    bank,
    fx,
    caps,
    capsCur,
    couponCaps,
    freebets,
    freebetsCur,
    tempo: tempoOf(attempts),
    roundStep,
    roundDir,
    roundWho,
    lock: stakeLock ?? undefined,
    odds: oddsLock ?? undefined,
    fees: feeLock ?? undefined,
    curs: curLock ?? undefined,
    free: freeLock ?? undefined,
    hand: handLock ?? undefined,
  };
  const oddsHeld = new Set(oddsLock?.forkId === row.id ? Object.keys(oddsLock.by) : []);
  const liveFork = paintLiveAsks(row.row.fork, liveBook.live ? liveBook.asks : null, oddsHeld);
  const card = liveBook.live
    ? rowWithFork(row, liveFork, lockInput)
    : rowWithLock(row, lockInput);
  const standings = useAccounts((s) => s.accounts);
  const uses = useAccounts((s) => s.uses);
  const paper = usePaper((s) => s.on);
  const bets = usePaper((s) => s.bets);
  const now = useScreen((s) => s.now);
  // Готовность расширения к биржевой ноге спрашивается один раз на карточку:
  // ответ дешёвый (без сети), но и он не нужен там, где биржевой ноги нет.

  const can = useExchange((s) => s.can);
  const buying = useExchange((s) => s.by[row.id]);
  const askCan = useExchange((s) => s.askCan);
  const recall = useExchange((s) => s.recall);
  const buyLeg = useExchange((s) => s.buy);
  const seats = useScreen((s) => s.seatLock[row.id]);
  const legsFirst = legsFirstOf(useProfile((s) => s.profile?.legsFirst));
  const legs = holdLegs(card.row.legs, seats, legsFirst);
  useLayoutEffect(() => {
    useScreen.getState().holdSeats(row.id, legOrder(card.row.legs, legsFirst).map(legSeat));
  }, [row.id, legsFirst]);
  const initiatorBy =
    typeof row.row.fork.initiatorBy === 'string' ? row.row.fork.initiatorBy : undefined;
  const withRub = hasRubBook(legs, meta);
  const unit = useShow((s) => s.unit);
  const gain = profitText(card.row.profit, card.row.roiPct, fx, unit);
  const staked = cardMoney(card.row.staked, fx, withRub);
  const payout = cardMoney(card.row.payout, fx, withRub);
  // Подробная математика раскрывается по желанию и живёт, пока карточка
  // открыта. Между карточками не запоминается: это справка к ЭТОЙ вилке.
  const [math, setMath] = useState(false);
  // Клик по числу правит кэф и сумму. Карандаш шапки по-прежнему
  // открывает комиссию, фрибет, валюту и третью ногу.
  const [calc, setCalc] = useState(false);
  useEffect(() => {
    setCalc(false);
  }, [row.id]);

  const dead = row.ghost !== null;


  // Точка, от которой считается сдвиг. Держится якорем и НЕ ползёт за лентой:
  // кэф редко прыгает разом, он ползёт (2.10 → 2.08 → 2.06 → 2.04), и сравнение
  // с прошлым кадром показывало бы крошечный последний шаг вместо всего пути от
  // цены, ради которой карточку открыли. Человек считает от того, что увидел.
  const shiftFrom = useRef<ReturnType<typeof snapOf> | null>(null);
  /** Что в ленте прямо сейчас: с этого места сверка переставляет якорь. */
  const tapeNow = useRef<ReturnType<typeof snapOf> | null>(null);
  const [shifted, setShifted] = useState<string | null>(null);
  useEffect(() => {
    shiftFrom.current = null;
    setShifted(null);
  }, [row.id]);
  // Якорь — лента, не карандаш: свой кэф в поле не должен писать
  // «вилка изменилась», это человек сам вбил.
  const tapeLegs = (row.row.fork.legs ?? []).map((leg) => ({
    source: leg.s,
    odds: leg.odds,
  }));
  const tapeRoiRaw = Number.parseFloat(row.row.fork.roi ?? '');
  const tapeRoi = Number.isFinite(tapeRoiRaw) ? tapeRoiRaw : card.row.roiPct;
  const tapeKey = `${tapeLegs.map((leg) => `${leg.source}:${leg.odds}`).join('|')}|${tapeRoi}`;
  // Деньги на первой ноге переставляют точку отсчёта: дальше человека касается
  // только та цена, под которую он их отдал. Отсчёт «от открытия карточки»
  // после ставки рассказывал бы про цену, которой он уже не распоряжается.
  const betAt = steps.first;
  useEffect(() => {
    if (betAt === null) return;
    shiftFrom.current = null;
    setShifted(null);
  }, [betAt]);
  useEffect(() => {
    const next = snapOf(tapeLegs, tapeRoi);
    tapeNow.current = next;
    if (dead) {
      setShifted(null);
      return;
    }
    if (shiftFrom.current === null) {
      shiftFrom.current = next;
      return;
    }
    // Пустой ответ пишется тоже: цена вернулась к якорю — новости больше нет,
    // и висеть на экране ей не за что.
    setShifted(
      shiftSay(
        shiftFrom.current,
        next,
        (source) => venueName(meta, source),
        betAt === null ? 'open' : 'bet',
      ),
    );
  }, [dead, tapeKey, meta, betAt]);

  // Точный адрес события и суммы по шагу ставки спрашиваются, пока человек
  // читает карточку. Спросить по нажатию нельзя: вкладку, открытую после
  // ожидания сети, браузер считает всплывающим окном и блокирует.
  const warm = useEntry((s) => s.warm);
  useEffect(() => {
    const total = stakeLock?.forkId === row.id && card.row.staked > 0 ? card.row.staked : bank;
    warm(row.id, total, fx, placeMark(row.row.fork, total, fx));
  }, [row.id, bank, fx, warm, card.row.staked, stakeLock]);

  const opened = steps.opened.some((at) => at !== null);
  const promiseKey = liveHoldKey(
    row.row.fork,
    `${stakeLock?.forkId === row.id ? stakeLock.form : ''}|${
      oddsLock?.forkId === row.id ? JSON.stringify(oddsLock.by) : ''
    }`,
  );
  useEffect(() => {
    if (!opened || pinned) return;
    return act.holdPromises(row.id, legs);
  }, [row.id, opened, pinned, promiseKey]);

  // Что видно перед ордером: заморожена ли лента и не пересчитываются ли деньги
  // прямо сейчас. Числа в строке считаются отложенно (`rows.ts`), и доли
  // секунды после правки банка на экране стоят прежние суммы: открыть по ним
  // вкладку не жалко, отправить ордер — жалко.
  const frozen = useScreen((s) => s.frozen);
  const sight = { frozen, stale: useDeferredValue(bank) !== bank || useDeferredValue(fx) !== fx };

  const exchange = legs.find((leg) => leg.asset !== null);
  useEffect(() => {
    if (exchange === undefined) return;
    if (can === null) void askCan();
    // Своя память о попытке умирает вместе со вкладкой, а деньги — нет.
    // Спрашиваем чужую: после перезагрузки страницы кнопка обязана знать, что
    // ордер по этой вилке уже уходил.
    void recall(row, exchange);
  }, [exchange, can, askCan, recall, row]);

  // Жест проверки: лента плюс текст на обеих страницах. Снимок кэфов
  // при открытии карточки сюда больше не входит — это сверка ленты с собой.
  const verifySeq = useRef(0);
  const [checked, setChecked] = useState<{
    at: number;
    ok: boolean;
    text: string;
    odds: string;
    pages?: { venue: string; closest: number | null; state: string }[];
  } | null>(null);
  const oddsNow = legs.map((leg) => leg.odds).join('/');
  // Браузер пустил только одну вкладку: об этом надо сказать сразу, иначе
  // человек ставит первую ногу, уверенный, что вторая контора уже открыта.
  const [blocked, setBlocked] = useState(false);

  // Процент, оторванный от денег, — главная ложь этого жанра. Помечаем случай,
  // когда он крупный, а прибыль мелкая, потому что срезал стакан или лимит
  // конторы. Свой банк сюда не входит: человек сам его задал.
  const lie = moneyLie(card.row);

  // «Открыть обе» не спрашивает ссылку, труп и повтор: без адреса откроется
  // дом площадки. Кнопки ног по-прежнему отличают матч от хаба.
  const linked = (leg: (typeof legs)[number]): boolean =>
    rememberHref(row.id, leg.source, leg.side, act.urlOf(row.id, leg)) !== null;
  // Исход обещает контору, не буфер. Дверь без события — хаб и имя площадки,
  // не «1-я карта — G2»: в поиск БК это почти никогда не то поле (30.08).
  const goPick = (leg: (typeof legs)[number], index = legs.indexOf(leg)): void => {
    if (callActions?.openLeg !== undefined && index >= 0) {
      callActions.openLeg(index);
      return;
    }
    const dest = rememberHref(row.id, leg.source, leg.side, act.urlOf(row.id, leg));
    if (dest === null) return;
    if (act.searchDoor(leg.source, dest)) void copy(act.matchQuery(row));
    if (act.urlOf(row.id, leg) !== null) act.openBook(row.id, leg);
    else act.openTab(dest, leg.source);
  };

  const tempo = tempoOf(attempts);
  const bet = bets.find((one) => one.id === row.id) ?? null;
  const outcome = bet === null ? null : settle(bet, tempo, now);

  // Отметки по порядку ног: третья есть только у трёхногих вилок (1-Х-2).
  const stamps = [steps.first, steps.second, steps.third ?? null];
  const polyAt = legs.findIndex((leg) => isPolymarket(leg.source));
  const polyLeg = polyAt >= 0 ? legs[polyAt]! : null;
  const fill = useFill(
    polyWallet,
    polyLeg,
    polyAt >= 0 ? (steps.opened[polyAt] ?? stamps[polyAt] ?? null) : null,
  );
  const [exitKick, setExitKick] = useState<{ n: number; kind: ExitKind }>({ n: 0, kind: 'bet' });

  // Голая нога — не «неудачная попытка», а отдельное состояние: выбора уже нет,
  // деньги стоят, и решать надо сейчас. Считается по тем же отметкам, что и всё
  // остальное в карточке (`naked.ts`).
  const naked = nakedOf(legs, stamps, steps.failed);

  /**
   * Открыть конторы перечисленных ног. `false` — браузер пустил не все вкладки.
   *
   * Само действие живёт в `act.ts`: те же вкладки открывает витрина и
   * клавиатура, и расходиться этим трём путям нельзя — вход в вилку один.
   */
  const openTabs = (list: typeof legs): boolean => {
    // Панель вилки у расширения — отдельный документ `/app?fork=…&panel=1`: свой
    // стор, свой сокет, своего кадра. Строки в нём может уже не быть, и тогда
    // панель открывается пустой сразу после клика. Закладку кладём здесь, в
    // момент жеста: это последняя точка, где вилка ещё точно есть.
    leaveSeed(row.id);
    return act.openTabs(row.id, list, act.pricedOf(row), (PRIVATE_MIRROR || RU_FEED) ? { onPlaced: ok => setBlocked(!ok) } : undefined);
  };

  const go = (index: 1 | 2 | 3): void => act.going(row, index);

  /** Что написать на кнопке биржевой ноги: считается из той же строки ленты. */
  const buyable = (leg: RowLeg): { can: boolean; text: string } => buyText(row, leg, sight);

  /** «Поставил»: деньги на кону. Только отсюда идут счёт дня и замер темпа. */
  const take = (index: 1 | 2 | 3): void => act.placed(card, index);

  // Залипшая цена: до нажатия, а не после. Самая частая причина «поставил
  // первую, а второй уже нет» на зеркальной ленте — см. `still.ts`.
  // Часы передаются нарочно: серверный покой приходит один раз и не обновляется,
  // а застывшая цена дельт не рождает вовсе — без `now` карточка навсегда
  // запомнила бы «стоит 20 с» ровно в самом опасном случае. Строка ленты считает
  // тем же способом (`rows.ts`), и разойтись эти два числа не имеют права:
  // человек читает их на одном экране (правка 11.08, восстановлено после того,
  // как я затёр её копированием файла поверх свежей базы).
  const stuck = stillness(row.row.fork, now);

  // «Проверить вилку»: обе страницы сейчас, не снимок ленты. Ставки не жмёт.
  const verify = (): void => {
    const at = Date.now();
    const mine = (verifySeq.current += 1);
    const mark = (row: {
      ok: boolean;
      text: string;
      pages?: { venue: string; closest: number | null; state: string }[];
    }): void => {
      if (verifySeq.current !== mine) return;
      // Сверка переставляет и точку отсчёта: человек только что посмотрел
      // свежие числа, и следующий сдвиг — это сдвиг от них. Иначе автострока
      // про путь от открытия карточки тут же затёрла бы ответ проверки.
      if (tapeNow.current !== null) shiftFrom.current = tapeNow.current;
      setShifted(null);
      setChecked({ at, odds: oddsNow, ...row });
    };
    const stuckText =
      stuck === null ? null : `${venueName(meta, stuck.source)} (${stillText(stuck)})`;
    const tape = forkCheck({
      dead,
      doubtDead: row.row.doubt === 'dead',
      stuck: stuckText,
      snap: { legs: [] },
      deskOn: false,
      nameOf: (venue) => venueName(meta, venue),
    });
    // Пустой snap здесь только чтобы лента отказала мгновенно. На страницы
    // не ходим, если вилка уже труп: три секунды на мёртвом — это опоздание.
    if (dead || row.row.doubt === 'dead' || stuck !== null) {
      mark(tape);
      return;
    }
    mark({ ok: false, text: 'смотрим страницы…' });
    act.refreshPromises(row.id, legs);
    act.retargetOpenBooks(row.id, legs);
    void snapPages(
      legs
        .filter((leg) => leg.source !== HAND_SOURCE)
        .map((leg) => {
          const raw = (row.row.fork.legs ?? []).find((one) => one.s === leg.source);
          const sel = (typeof raw?.sel === 'string' && raw.sel.includes('~') ? raw.sel : null)
            ?? leg.sel
            ?? undefined;
          return {
            venue: leg.source,
            odds: leg.odds,
            url: act.urlOf(row.id, leg),
            who: leg.who,
            side: leg.side,
            ...(sel ? { sel } : {}),
            ...(leg.kind ? { kind: leg.kind } : {}),
            ...(leg.period ? { period: leg.period } : {}),
            ...(leg.line ? { line: leg.line } : {}),
            ...(leg.sport ? { sport: leg.sport } : {}),
            ...(leg.eventLine === 'live' || leg.eventLine === 'prematch'
              ? { eventLine: leg.eventLine }
              : {}),
            ...(leg.marketKey ? { marketKey: leg.marketKey } : {}),
            ...(leg.name ? { name: leg.name } : {}),
          };
        }),
    ).then((snap) => {
      const say = forkCheck({
        dead,
        doubtDead: row.row.doubt === 'dead',
        stuck: stuckText,
        snap,
        deskOn: useDesk.getState().on,
        nameOf: (venue) => venueName(meta, venue),
      });
      mark({
        ...say,
        pages: snap?.legs.map((one) => ({
          venue: one.venue,
          closest: one.closest,
          state: one.state,
        })),
      });
      if (snap !== null) {
        rememberSite(row.id, row.row.fork, snap.legs);
      }
      if (snap?.clips !== undefined && snap.clips.length > 0) {
        notePages(snap.clips, { forkId: row.id });
      }
    });
  };

  const pageQuotes = (checked?.pages ?? []).filter(
    (one) =>
      one.state === 'moved' &&
      one.closest !== null &&
      Number.isFinite(one.closest) &&
      one.closest > 1,
  );
  const applyPageOdds = (): void => {
    for (const one of pageQuotes) {
      if (one.closest === null) continue;
      const leg = legs.find((row) => row.source === one.venue);
      if (leg === undefined || leg.source === HAND_SOURCE) continue;
      if (leg.unit === 'share') {
        const cents = 100 / one.closest;
        if (cents <= 0 || cents >= 100) continue;
        setOddsQuote(row.id, one.venue, cents.toFixed(1));
        continue;
      }
      setOddsQuote(row.id, one.venue, String(one.closest));
    }
  };

  const liveCount = (row.row.fork.legs ?? []).length;
  const canHand = liveCount === 2;
  const hasHand = handLock?.forkId === row.id;
  const [coverSide, setCoverSide] = useState<string | null>(null);
  const [coverPick, setCoverPick] = useState<CoverQuote | null>(null);
  useEffect(() => {
    setCoverSide(null);
    setCoverPick(null);
  }, [row.id]);
  const canCover = !dead && liveCount === 2 && !hasHand;
  const coverOpp =
    coverSide !== null
      ? oppositeLeg(legs, coverSide)
      : coverPick !== null
        ? oppositeLeg(legs, coverPick.side ?? '')
        : undefined;
  const toggleHand = (): void => {
    setCoverPick(null);
    setCoverSide(null);
    if (hasHand) {
      setHandOdds(row.id, null);
      if (stakeLock?.forkId === row.id && stakeLock.source === HAND_SOURCE) setStakeLock(null);
      if (freeLock?.forkId === row.id && freeLock.source === HAND_SOURCE) setFreeLeg(row.id, null);
      setFeeQuote(row.id, HAND_SOURCE, null);
      return;
    }
    if (canHand) setHandOdds(row.id, '2.000');
  };

  // Причины сомневаться собираются в один блок НАД действием. Раньше они стояли
  // последней строкой карточки одиннадцатым кеглем: экран одновременно кричал
  // «+2 571 ₽» и шептал «одна из цен неверная». Порядок чтения обязан совпадать
  // с порядком решения — сначала «ставить ли вообще», потом «сколько и куда».
  //
  // Непроверенное (`checks.unknown`) в этот блок больше не попадает. На боевой
  // ленте это неизменная пара `spread` + `motion`: стакана у букмекера нет по
  // природе площадки, и «сверить спред не с чем» стояло под каждой вилкой без
  // единого исключения (11.08, владелец). Довод, который нельзя не сказать, —
  // это не довод, а фон.
  // Разные правила приходят к одному выводу: «прыжок цены» и «высокий кэф
  // против низкого» оба означают «цена неверна». Вывод показываем один раз, но
  // доказательства сцепляем — молчать про второе правило нельзя, оно
  // независимое, и вдвоём они весят больше, чем каждое по себе.
  const stops: { say: string; why: string | null }[] = [];
  // Покой обычной конторы и каденция трубы (`stale`/`frozen`) сюда не входят:
  // это час котировки, не «вилки нет». Полик и предикт — другое: ордера уже
  // не принимают, а стакан ещё рисуется. Их долгий покой поднимаем в блок.
  // Залипший бук по-прежнему виден у кнопки сверки.
  const mute = shareStill(row.row.fork, now);
  const add = (item: { say: string; why: string | null }): void => {
    const same = stops.find((one) => one.say === item.say);
    if (same === undefined) stops.push(item);
    else if (item.why !== null && same.why !== item.why) {
      same.why = same.why === null ? item.why : `${same.why} · ${item.why}`;
    }
  };
  // Одна фраза на оба источника вывода «не успеете»: серверное правило
  // `too_short` и наш замер по темпу человека. Совпадение строк и есть склейка —
  // `add` дедуплицирует по `say`, поэтому дважды об одном блок не скажет.
  const shortSay = shortLifeSay(row.row.fork, tempo);
  for (const rule of row.row.fork.rl ?? []) {
    // «входа нет» уже написано на самой ноге — второй раз в красном блоке это
    // читается как ещё одна угроза, хотя угрозы нет. Каденция трубы (`stale`)
    // сюда же не входит: 15 с у Фонбета — ещё живая цена, не смерть рынка.
    if (
      rule.c === 'no_link' ||
      rule.c === 'unverifiable' ||
      rule.c === 'stale' ||
      rule.c === 'frozen' ||
      rule.c === 'delayed' ||
      rule.c === 'unwitnessed'
    ) {
      continue;
    }
    const said = RULE_WORDS[rule.c];
    // Свой перевод всегда. Резать серверную фразу по тире нельзя: хвост
    // «снята» с меткой «внимание» читался как «внимание снята» (05.09, Антон).
    // Числа сервера — только в подсказке. Числа жизни вилки — в правой фразе.
    add({ say: said ?? 'с этой вилкой что-то не так', why: rule.t });
  }
  if (row.row.doubt === 'fast') add({ say: TOO_SHORT_SAY, why: null });
  if (mute !== null) add({ say: shareStillSay(mute.source), why: stillText(mute) });

  // Тон блока — по худшему из доводов (07.08, владелец): красный «опасно»
  // только когда цены, скорее всего, уже неверны; жёлтый «осторожно» — вилка
  // живая, но быстрая. Третьего разряда нет: «спокойно» и «мы не всё проверили»
  // означают одно и то же — отсутствие блока.
  // Замерший полик красим как мёртвую цену: иначе при doubt=null блок
  // становится серой заметкой, и Антон снова не остановится.
  const level = row.row.doubt === 'dead' || mute !== null ? 'dead' : row.row.doubt;
  // Плашка уже сказала «вилки нет». Герой с +$ это отменяет: тестер читает
  // банк. Пока первой ноги нет — крупный плюс на трупе не рисуем.
  const sellCorpse = (level === 'dead' || stuck !== null) && steps.first === null;
  // Слева — что случилось, словами новичка. Не штамп «внимание»: штамп плюс
  // обрубок серверной фразы и дал «внимание снята». Справа — числа, если есть,
  // и что делать руками (NN/g: what happened + what to do, без ярлыка Warning).
  const stopWord = stops[0]?.say ?? '';
  const stopSay = ((): string => {
    const extra = stops
      .slice(1)
      .map((one) => one.say)
      .join(' · ');
    const life = stops.some((one) => one.say === TOO_SHORT_SAY) ? shortSay : null;
    const tip =
      level === 'dead'
        ? 'ставку могут не принять. сначала сверьте кэф на сайте'
        : level === 'fast'
          ? 'вторую ставку можно не успеть'
          : 'откройте контору и сверьте кэф';
    return [extra, life, tip].filter((bit) => bit !== null && bit !== '').join('. ');
  })();
  // Серверные числа («прыжок 1.540→3.750 при пороге 0.25») ушли в подсказку.
  // Они точные и нужны тому, кто собрался с нами спорить, но спорят единицы, а
  // колонку читали все — и именно она делала блок нечитаемым.
  const stopWhy = stops
    .map((one) => one.why)
    .filter((why): why is string => why !== null)
    .join(' · ');

  const title = matchTitleInOrder(row.row.fork, legs);
  const kind = polyquant ? [leagueTitle(row.row.fork), ...new Set((row.row.fork.legs ?? []).map(leg => polyquantMarket(leg.name || leg.side)))].filter(Boolean).join(' · ') : cardKind(row.row.fork);
  const orderWhy = leadWhy(legs, legsFirst);
  const rareWhy = rareNote(row.row.fork, legs, (code) => venueName(meta, code));
  // Часы те же, что возраст вилки: серверное «через N минут» протухло бы
  // между кадрами, а этот `now` уже тикает каждую секунду.
  const when = whenText(row.row.fork, now);
  // В буфер — страница события, не имя команд. Хаб без матча сюда не берём:
  // «https://bc.game/sports» событием не является.
  const eventPage = (leg: (typeof legs)[number]): string | null => {
    const page = act.urlOf(row.id, leg);
    if (page === null || act.searchDoor(leg.source, page)) return null;
    return page;
  };
  const eventPages = [...new Set(legs.map(eventPage).filter((page): page is string => page !== null))];

  return (
    <div className={`nx-det${calc ? ' calc' : ''}`}>
      <div className="nx-det__body">
      {/* Шапка: что за матч, и две кнопки распоряжения карточкой.
          Название — текст, не ссылка: одна шапка на две конторы. Иконка кладёт
          адреса событий, не имя матча. Рабочий вход без deep-link — марка и
          кнопка, они по-прежнему кладут matchQuery в поиск двери. */}
      <div className={`nx-det__head${bookCard ? ' book' : ''}`}>
        {/* Рынок в шапке всегда: в ленте исход стоит в строке над карточкой,
            но взгляд уже на плитках, и «гонка до 10» там пропадает (14.10).
            Команды — в порядке плиток, не домашний–гость из протокола. */}
        {alone && <Sign sport={row.row.fork.sport} name={row.row.fork.sportName} />}
        <span className="nx-det__title">
          {/* Нет команд — «матч», не `v5:f:…`: это номер кадра, человек ищет имена. */}
          <span className="nx-det__name">
            <span className="nx-det__match">{title === '' || isCodeName(title) ? 'матч' : title}</span>
            {eventPages.length > 0 && (
              <Clip
                text={eventPages.join('\n')}
                what="ссылку на событие"
                className="nx-clip nx-det__clip"
              />
            )}
          </span>
          {bookCard ? (
            (when !== null || kind !== '') && (
              <span className="nx-det__sub">
                {when !== null && (
                  <span className="nx-det__when">
                    {when.split(/(\d+)/).map((bit, i) =>
                      /^\d+$/.test(bit) ? <b key={i}>{bit}</b> : bit,
                    )}
                  </span>
                )}
                {when !== null && kind !== '' && <span aria-hidden="true">·</span>}
                {kind !== '' && <span className="nx-det__kind">{kind}</span>}
              </span>
            )
          ) : (
            kind !== '' && <span className="nx-det__kind">{kind}</span>
          )}
        </span>
        {!bookCard && when !== null && (
          <span className="nx-det__when">
            {when.split(/(\d+)/).map((bit, i) =>
              /^\d+$/.test(bit) ? <b key={i}>{bit}</b> : bit,
            )}
          </span>
        )}
        <span className="nx-det__gap" />
        {pilotUser && !alone && (
          <button
            type="button"
            className={`nx-det__check${
              shifted !== null
                ? ' bad'
                : checked !== null && checked.odds === oddsNow && now - checked.at <= CHECK_FRESH_MS
                  ? checked.ok
                    ? ' ok'
                    : ' bad'
                  : ''
            }`}
            title={callActions?.verify !== undefined ? 'проверить актуальность вилки' : 'проверить вилку'}
            aria-label={callActions?.verify !== undefined ? 'проверить актуальность вилки' : 'проверить вилку'}
            onClick={callActions?.verify ?? verify}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M3.2 8.4 6.4 11.5 12.8 4.6" />
            </svg>
          </button>
        )}
        {pilotUser && (
          <button
            type="button"
            className={`nx-det__math${math ? ' on' : ''}`}
            title={math ? 'скрыть математику' : 'математика'}
            aria-label={math ? 'скрыть математику' : 'математика'}
            aria-pressed={math}
            onClick={() => setMath(!math)}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M12.6 3.2H4.4L9.4 8 4.4 12.8h8.2" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className={`nx-det__edit${calc ? ' on' : ''}`}
          title={
            calc
              ? 'закрыть правку: набитое остаётся, лента его не затирает'
              : 'править суммы, кэфы, комиссию и фрибет'
          }
          aria-label={calc ? 'закрыть правку расчёта' : 'править расчёт'}
          aria-pressed={calc}
          onClick={() => setCalc((on) => !on)}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M11.6 2.6 13.4 4.4 6.2 11.6 4 12l.4-2.2z" />
            <path d="M10.4 3.8 12.2 5.6" />
          </svg>
        </button>
        {!alone && (
          <>
            <button
              type="button"
              className="nx-det__pop"
              title="открыть вилку отдельным окном, его удобно положить поверх сайта конторы"
              aria-label="открыть отдельным окном"
              onClick={() => popOut(row.id)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="M6.5 3.5h-3v9h9v-3" />
                <path d="M9.5 3.5h3v3" />
                <path d="M12.5 3.5 7.5 8.5" />
              </svg>
            </button>
            {shelf === 'hidden' && !dead && liveStill && (
              <button type="button" className="nx-det__back" onClick={() => unhide(row.id)}>
                вернуть
              </button>
            )}
            <button
              type="button"
              className="nx-det__shut"
              title="скрыть вилку"
              aria-label="скрыть вилку"
              onClick={() => hide(row.id)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </>
        )}
      </div>
      {/* Смерть вилки карточка сообщает сама, а не исчезает (07.08, владелец):
          раньше строка пропадала из-под глаз посреди чтения, а человек, уже
          поставивший первую ногу, оставался вообще без цифр. */}
      {/* Блок появляется только когда есть о чём предупредить: залипшая цена,
          правило гейта, нехватка времени. Оговорки «мы не всё проверили» сюда
          больше не попадают — они и делали плашку постоянной, см. `stops`.
          Гасить блок по разряду тревоги было бы ошибкой: «кэф не бьётся с
          рынком» приходит без разряда, и это настоящая угроза деньгам. */}
      {stops.length > 0 && (
        <div
          className={`nx-det__stop${level === 'fast' ? ' warn' : level === null ? ' note' : ''}`}
        >
          <u>{stopWord}</u>
          {/* Доказательство — подсказкой, а не колонкой. Держать его в `title`
              честнее, чем удалить: спорить с нами по-прежнему есть чем. */}
          <span title={stopWhy === '' ? undefined : stopWhy}>{stopSay}</span>
        </div>
      )}
      {/* Итог отдельным окном обязан стоять крупно: в ленте прибыль написана в
          строке над карточкой, а в окне строки нет вовсе — там прибыль была
          видна только тому, кто раскроет «математику» (08.08, владелец).
          Стоит ПОСЛЕ блока сомнений и до кнопок: порядок чтения — «ставить ли
          вообще», потом «сколько выйдет», потом «куда нажимать». Продать
          крупным плюсом то, от чего мы сами отговариваем строчкой ниже, значит
          отменить собственное предупреждение. */}
      {/* Колл хранит расчёт отправленного плана даже после исчезновения вилки.
          Состояние снимка не скрывает числа и не означает свежие котировки. */}
      {alone && (callActions !== undefined || !sellCorpse) && (
        <div className="nx-det__hero">
          <span className="nx-det__heroL">
            {callActions !== undefined ? 'прибыль по сохранённому расчёту' : 'прибыль при любом исходе'}
          </span>
          <b className="nx-det__heroV">{gain}</b>
          {unit !== 'pct' && (
            <i className="nx-det__heroP">
              {card.row.roiPct.toFixed(2)} % от вложенного
              {typeof card.row.fork.roiExecutable === 'string' && card.row.fork.roiExecutable !== ''
                ? ` · подтверждённый ${card.row.fork.roiExecutable}%`
                : ''}
            </i>
          )}
          <dl className="nx-det__heroF">
            <div>
              <dt>сумма ставок</dt>
              <dd>{cardSay(staked)}</dd>
            </div>
            <div>
              <dt>вернётся</dt>
              <dd>{cardSay(payout)}</dd>
            </div>
          </dl>
        </div>
      )}
      {/* Каждая нога — своя плитка: это два отдельных действия в двух разных
          конторах, а не два столбца одной таблицы.
          Очерёдность держат цифра шага и приглушённая вторая кнопка — и больше
          ничего. Подписи «первой: ссылка ведёт в раздел» и «лучше после первой»
          сняты 11.08 по требованию владельца: они стояли по строке на плитку,
          повторяли то, что и так сказано номером, а на ноге без точного адреса
          дублировали подпись под кнопкой слово в слово. Дороже прочего они
          стоили выравниванием: подпись переносилась на вторую строку только в
          одной плитке, и суммы двух ног съезжали друг относительно друга —
          именно это владелец назвал «криво косо».
          Строку «ссылки нет» не снимаем: 17.08 Рафик — «укажи обязательно».
          Она живёт в общем ряду подвала, а не под одной кнопкой, иначе снова
          поднимает и кнопку, и сумму соседней плитки.
          Приглушённая, а не запертая: очерёдность — совет, и вилочник вправе
          начать с той конторы, где очередь длиннее (08.08, владелец).
          Одна строка над плитками — только когда первая нога стоит первой по
          свойству площадки (`leadWhy`), а не по общему риску. */}
      {rareWhy !== null && <p className="nx-det__rare">{rareWhy}</p>}
      {!polyquant && orderWhy !== null && <p className="nx-det__orderWhy">{orderWhy}</p>}
      <PredictFacts fork={row.row.fork as Fork} />
      <ol className={`nx-det__steps${legs.length > 2 || coverPick !== null ? ' trio' : ''}${bookCard ? ' book' : ''}`}>
        {legs.map((leg, i) => {
          const first = i === 0;
          const last = i === legs.length - 1;
          const done = stamps[i] !== null;
          // Три состояния ноги вместо двух: не начата → контора открыта, ждём
          // ответа «поставили?» → поставлена. Среднее и есть та честность, из-за
          // которой темп перестал браться из воздуха.
          const going = !done && steps.opened[i] !== null;
          const failed = steps.failed[i];
          // Нога открывается только после предыдущей: у трёхногой вилки цепочка
          // та же, просто длиннее — 1, потом 2, потом 3. По мёртвой карточке
          // НАЧАТЬ нельзя — а вот закрыть начатое можно и нужно.
          const ready = (i === 0 || stamps[i - 1] !== null) && (!dead || steps.first !== null);
          const share = leg.unit === 'share';
          const isHand = leg.source === HAND_SOURCE;
          const freeOn = leg.freebet === true || (freeLock?.forkId === row.id && freeLock.source === leg.source);
          const feeEntry = feeLock?.forkId === row.id ? feeLock.by[leg.source] : undefined;
          const feeLocked = feeEntry !== undefined;
          const feeForm = feeEntry ?? '';
          const oddsForm = isHand
            ? (handLock?.odds ?? leg.odds)
            : oddsLock?.forkId === row.id && oddsLock.by[leg.source] !== undefined
              ? oddsLock.by[leg.source]!
              : share
                ? centsText(leg.cents ?? 0)
                : leg.odds;
          const oddsLocked = isHand
            ? hasHand
            : Boolean(oddsLock?.forkId === row.id && oddsLock.by[leg.source] !== undefined);
          const lockOdds = (form: string): void => {
            if (isHand) setHandOdds(row.id, form);
            else setOddsQuote(row.id, leg.source, form);
          };
          const unlockOdds = (): void => {
            if (isHand) setHandOdds(row.id, null);
            else setOddsQuote(row.id, leg.source, null);
          };
          // Нога «против»: крупно — то, что человек ВВОДИТ на бирже (ставка
          // соперника), а риск, который спишется со счёта, назван рядом.
          // Показать крупно риск значило бы, что человек введёт его в форму —
          // и примет чужую ставку вдвое больше расчётной.
          // На бирже в форме два поля — цена и количество, — и оба должны быть
          // названы порознь, каждое там, где стоит его конторский двойник. Цена
          // встаёт на место кэфа, количество — на место суммы. Слепленные в одну
          // строку «61 ¢ × 92.0» они читались как арифметика, хотя это две
          // разные ячейки чужой формы. Крупно — шеры, их же кладут в купон.
          // Доллары уже стоят строкой «лимиткой · $»: повторять их сверху
          // значит спрятать количество, которое человек переписывает в Shares.
          // По рынку сайт Polymarket ждёт доллары, не шары: крупно и в буфер — они.
          const poly = share && isPolymarket(leg.source)
            ? polyMarketCheck(leg, legs.length === 2 ? legs[1 - i] : undefined, liveBook.asks)
            : null;
          const marketUsd = act.marketStake(leg);
          const byMarket = marketUsd !== null;
          const copied = marketUsd ?? formOf(leg, fx, meta);
          const as = share
            ? lockAsOf(stakeLock?.forkId === row.id ? stakeLock : null, leg.source, exchangeStake)
            : undefined;
          const edited = editAmount(leg, fx, meta, as);
          const amount = byMarket
            ? `${marketUsd} $`
            : share
              ? `${(leg.shares ?? 0).toFixed(1)} шер`
              : moneyAmount(leg, fx, meta);
          const flipShareUnit = (): void => {
            if (as === undefined) return;
            const to = as === 'usd' ? 'share' : 'usd';
            setExchangeStake(to);
            if (stakeLock?.forkId === row.id && stakeLock.source === leg.source) {
              setStakeLock(
                stakeLockBody(
                  row.id,
                  leg.source,
                  convertStakeForm(stakeLock.form, as, to, leg.cents),
                  leg.unit,
                  to,
                ),
              );
            }
          };
          const account = accountNote(
            standings[leg.source]?.standing,
            pressureOf(uses, leg.source, Date.now()),
          );
          // Слоты плитки всегда одни и те же: сетка шагов выравнивает ряды
          // между ногами, и пустой слот обязан остаться в разметке — иначе
          // красная строка одной ноги снова поднимет сумму и кнопку другой.
          const polyOn = leg.asset !== null && can !== null && !done;
          const bookLeg = bookCard && isPolymarket(leg.source) && leg.unit === 'share';
          const href = rememberHref(
            row.id,
            leg.source,
            leg.side,
            leg.source === 'csgopositive' ? act.hrefOf(row.id, leg) : act.urlOf(row.id, leg),
          );
          const page = eventPage(leg);
          const wire = row.row.fork.legs?.find(
            (one) => one.s === leg.source && one.side === leg.side,
          );
          const note = polyquant ? (act.searchDoor(leg.source, href) ? 'Поиск матча' : null) : entryNote(leg, wire?.eid);
          const voided = voidNote(leg, row.row.fork);
          const needMarket = polyquant ? null : marketNote(leg, row.row.fork);
          return (
            <li
              className={`nx-det__step${done ? ' done' : ''}${ready ? '' : ' wait'}${bookLeg ? ' book' : ''}`}
              key={legSeat(leg)}
            >
              {/* Шапка плитки: номер шага и контора одной строкой, по левому
                  краю. Раньше номер висел отдельной колонкой слева, а всё
                  остальное лепилось к нему справа — на широком окне это
                  читалось как один разъехавшийся список, а не как две
                  ставки. */}
              <span className="nx-det__top">
                <span className="nx-det__no" aria-hidden="true">
                  {i + 1}
                </span>
                <InitiatorDot
                  initiator={leg.initiator}
                  initiatorCount={leg.initiatorCount}
                  by={initiatorBy}
                  className="nx-det__ini"
                />
                {/* Марка, а не строчка серого текста: с двумя чужими вкладками
                    перед глазами вопрос к плитке первый и один — «это какая из
                    двух». Цвет отвечает на него узнаванием, название — чтением
                    (08.08, владелец: «хотел бы логотипы видеть»). */}
                <Brand
                  code={leg.source}
                  name={venueName(meta, leg.source)}
                  href={href}
                  onOpen={
                    href === null
                      ? undefined
                      : () => {
                          if (callActions?.openLeg !== undefined) {
                            callActions.openLeg(i);
                            return;
                          }
                          act.openBook(row.id, leg);
                        }
                  }
                />
                {/* Ссылку можно взять, не уходя на площадку: из РФ половина
                    вкладок не грузится, адрес кладут в аккаунт с прокси.
                    Правый клик по марке делает то же. Имя в <a> не кладём. */}
                {href !== null && !pilotUser && (
                  <span className="nx-det__href">
                    <Grab text={href} what="ссылку на контору">
                      ссылка
                    </Grab>
                  </span>
                )}
                {!pilotUser && tileLag(row.row.fork, leg.source, meta, now) !== null && (
                  <span className="nx-det__lag" title="сколько эта цена стоит без движения">
                    {tileLag(row.row.fork, leg.source, meta, now)}
                  </span>
                )}
              </span>
              {/* Две колонки, а не столбик по центру: слева что отметить,
                  справа сколько внести. По центру строки разной длины
                  складывались в пирамиду, а углы плитки пустовали (08.08,
                  владелец). Пара «исход — сумма» так же лежит в витрине и так
                  же стоит на странице конторы, где её и сверяют. */}
              <div className="nx-det__take">
                {/* Исход и кэф — двумя строками текста, а не таблицей с
                    подписями. Подписи «исход» и «кэф» слева от значений съедали
                    треть плитки и объясняли то, что и так очевидно из самих
                    значений (08.08, владелец: «перегруз, минималистичнее»).
                    Слово «кэф» осталось при числе: 2.500 без него читается как
                    что угодно. */}
                <span className="nx-det__pick-row">
                  <span className="nx-cover__hit">
                    <span
                      className={href !== null ? 'nx-det__pick nx-det__pick--go' : 'nx-det__pick'}
                      role={href !== null ? 'link' : undefined}
                      tabIndex={href !== null ? 0 : undefined}
                      title={
                        href === null
                          ? undefined
                          : act.searchDoor(leg.source, href)
                            ? 'открыть контору и скопировать название матча'
                            : 'открыть контору'
                      }
                      onPointerDown={
                        href !== null ? (event) => event.stopPropagation() : undefined
                      }
                      onClick={
                        href !== null
                          ? (event) => {
                              event.stopPropagation();
                              goPick(leg, i);
                            }
                          : undefined
                      }
                      onKeyDown={
                        href !== null
                          ? (event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return;
                              event.preventDefault();
                              event.stopPropagation();
                              goPick(leg, i);
                            }
                          : undefined
                      }
                    >
                      <PickSaid text={betOn(leg, row.row.fork)} />
                    </span>
                    {page !== null && (
                      <Clip
                        text={page}
                        what="ссылку на событие"
                        className="nx-clip nx-det__clip"
                      />
                    )}
                    {canCover && (
                      <CoverStrip
                        open={coverSide === leg.side}
                        label={betOn(leg, row.row.fork)}
                        onToggle={() =>
                          setCoverSide((cur) => (cur === leg.side ? null : leg.side))
                        }
                      />
                    )}
                  </span>
                </span>
                <span className="nx-det__odds">
                  {/* У биржи вместо кэфа цена шара в центах: её и вводят в
                      форму. Кэф под ней мелким — он ни в какое поле не идёт, но
                      им вилку сравнивают с конторской и сверяют с лентой.

                      Каждое число — своим куском разметки, а не общей строкой:
                      строка переносится по пробелам, и в узкой плитке «цена 54 ¢
                      кэф 1.886» рассыпалась в лесенку из четырёх строк.

                      Карандаш — тот же жест, что у суммы. Контора правит кэф,
                      биржа — центы (вероятность). Свой ввод лента не затирает. */}
                  {share ? (
                    <>
                      <OddsEdit
                        kind="cents"
                        form={oddsForm}
                        locked={oddsLocked}
                        armed={calc}
                        live={calc}
                        clickable={!calc}
                        lead={marketPilot ? '' : 'цена '}
                        tail="¢"
                        onLock={lockOdds}
                        onUnlock={unlockOdds}
                      >
                        <span>
                          {marketPilot ? '' : 'цена '}
                          {centsText(leg.cents ?? 0)} ¢
                        </span>
                      </OddsEdit>
                      <s>{marketPilot ? leg.odds : `кэф ${leg.odds}`}</s>
                      {leg.ownOdds && leg.centsLive !== undefined && leg.centsLive !== null && (
                        <i className="nx-det__tape">лента {centsText(leg.centsLive)} ¢</i>
                      )}
                      {(calc || feeLocked) &&
                        (calc ? (
                          <FeeEdit
                            form={feeForm}
                            locked={feeLocked}
                            onLock={(form) => setFeeQuote(row.id, leg.source, form)}
                            onUnlock={() => setFeeQuote(row.id, leg.source, null)}
                          />
                        ) : (
                          <span className="nx-fee own">комиссия {feeForm} %</span>
                        ))}
                    </>
                  ) : (
                    <>
                      <OddsEdit
                        kind="odds"
                        form={oddsForm}
                        locked={oddsLocked}
                        armed={calc}
                        live={calc}
                        clickable={!calc}
                        lead="кэф "
                        onLock={lockOdds}
                        onUnlock={unlockOdds}
                      >
                        <span>кэф {leg.odds}</span>
                      </OddsEdit>
                      {leg.ownOdds && leg.oddsLive !== undefined && leg.oddsLive !== leg.odds && (
                        <i className="nx-det__tape">лента {leg.oddsLive}</i>
                      )}
                      {(calc || feeLocked) &&
                        (calc ? (
                          <FeeEdit
                            form={feeForm}
                            locked={feeLocked}
                            onLock={(form) => setFeeQuote(row.id, leg.source, form)}
                            onUnlock={() => setFeeQuote(row.id, leg.source, null)}
                          />
                        ) : (
                          <span className="nx-fee own">комиссия {feeForm} %</span>
                        ))}
                      {!leg.lay &&
                        !isHand &&
                        (calc || freeOn) &&
                        (calc ? (
                          <FreeEdit
                            on={freeOn}
                            onToggle={() => {
                              if (freeOn) {
                                setFreeLeg(row.id, null);
                                return;
                              }
                              setFreeLeg(row.id, leg.source);
                              setStakeLock(stakeLockBody(row.id, leg.source, edited, leg.unit, as));
                            }}
                          />
                        ) : (
                          <span className="nx-free on">фрибет</span>
                        ))}
                    </>
                  )}
                </span>
                <span className="nx-det__amount">
                  <AmountEdit
                    form={edited}
                    locked={stakeLock?.forkId === row.id && stakeLock.source === leg.source}
                    armed={calc}
                    live={calc}
                    clickable={!calc}
                    tail={
                      share
                        ? 'шер'
                        : amount.includes('$')
                          ? '$'
                          : amount.includes('₽')
                            ? '₽'
                            : undefined
                    }
                    onLock={(form) =>
                      setStakeLock(stakeLockBody(row.id, leg.source, form, leg.unit, as))
                    }
                    onUnlock={() => {
                      if (stakeLock?.forkId === row.id && stakeLock.source === leg.source) {
                        setStakeLock(null);
                      }
                    }}
                  >
                    <b>{amount}</b>
                  </AmountEdit>
                  {calc && share && as !== undefined ? (
                    <ShareUnitEdit as={as} onPick={flipShareUnit} />
                  ) : calc && !share ? (
                    <CurEdit
                      cur={isRubStake(leg, meta) ? 'RUB' : 'USD'}
                      venue={catalogCur(leg, meta)}
                      onPick={(next) => setCurQuote(row.id, leg.source, next)}
                    />
                  ) : null}
                  {/* Копирование отдельной кнопкой, а не только нажатием на
                      число: после того как контора открыта, кнопка плитки
                      спрашивает «поставил?» и суммы в буфер больше не кладёт —
                      а именно тогда её и переспрашивают. */}
                  <button
                    type="button"
                    className="nx-det__copy"
                    title={`скопировать ${amount}`}
                    aria-label={`скопировать ${amount}`}
                    onClick={() => void copy(copied)}
                  >
                    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                      <path d="M10.5 3.5v-1h-8v8h1" />
                    </svg>
                  </button>
                  {/* Подпись под суммой — только когда она сообщает НОВОЕ:
                      биржевые деньги устроены иначе. «Сумма округлена» стояла
                      на каждой конторской ноге и не меняла ни одного действия
                      человека — снята (07.08, владелец). */}
                  {(share || leg.lay) && !marketPilot && (
                    <i>
                      {share
                        ? `лимиткой · ${(leg.usd ?? 0).toFixed(2)} $`
                        : `ставка против · биржа заморозит ${
                            isRubStake(leg, meta) || !(fx > 0)
                              ? `${rub(leg.rub)} ₽`
                              : `${usd(leg.rub / fx)} $`
                          }`}
                    </i>
                  )}
                  {voided !== null && !marketPilot && (
                    <i
                      className={`nx-det__void${voided.risk ? ' risk' : ''}${voided.loud ? ' loud' : ''}`}
                    >
                      {voided.text}
                    </i>
                  )}
                </span>
                {!polyquant && marketPilot && isPolymarket(leg.source) && leg.unit === 'share' && (
                  <div className="nx-det__book">
                    <div className="nx-det__polybar">
                      <PolyOrderEdit order={polyOrder} onPick={setPolyOrder} />
                      <span className="nx-det__bookmeta">
                        <span>
                          {byMarket && poly !== null
                            ? `~${poly.fill.net.toDecimalPlaces(1, Decimal.ROUND_DOWN).toFixed(1)} шер`
                            : `${marketUsd ?? (leg.usd ?? 0).toFixed(2)} $`}
                        </span>
                        {leg.cents != null && leg.cents > 0 && <b>{centsText(leg.cents)} ¢</b>}
                      </span>
                    </div>
                    <PolyBookLadder
                      asks={liveBook.asks}
                      usd={leg.marketUsd}
                      ourPrice={leg.cents != null && leg.cents > 0 ? leg.cents / 100 : null}
                      live={liveBook.live}
                      at={liveBook.at}
                      eat={polyOrder === 'market'}
                      say={
                        liveBook.empty
                          ? 'стакан пуст'
                          : liveBook.asks == null || liveBook.asks.length === 0
                            ? 'ищем живой стакан'
                            : null
                      }
                    />
                    {voided !== null && (
                      <i
                        className={`nx-det__void${voided.risk ? ' risk' : ''}${voided.loud ? ' loud' : ''}`}
                        title={voided.text}
                      >
                        {voided.short}
                      </i>
                    )}
                  </div>
                )}
              </div>
              {/* Биржевая нога покупается отсюда: у биржи нет ни страницы
                  матча, ни формы — только токен, цена и подпись, и вся дорога,
                  которую человек проходит руками на конторе, здесь равна нулю.
                  Подписывает расширение своим ключом; лента считает потолок
                  цены и молчит, когда расширения нет. Кнопка «поставил» рядом
                  остаётся: купить можно и руками на самой бирже. */}
              <div className={`nx-det__poly${polyOn ? '' : ' vacant'}`}>
                {leg.asset !== null && can !== null && !done ? (
                  can.ready ? (
                    <>
                      <button
                        type="button"
                        className="nx-det__buy"
                        disabled={
                          buying?.state === 'work' ||
                          (dead && steps.first === null) ||
                          !buyable(leg).can
                        }
                        onClick={() => void buyLeg(row, leg, (i + 1) as 1 | 2 | 3, sight)}
                      >
                        {buying?.state === 'work'
                          ? 'подписываем и отправляем…'
                          : buyable(leg).text}
                      </button>
                      {buying !== undefined && buying.state !== 'work' && (
                        <i className={`nx-det__polySay ${buying.state}`}>{buying.say}</i>
                      )}
                    </>
                  ) : bookLeg ? null : (
                    <em>{can.why}. биржевую ногу пока ставим руками</em>
                  )
                ) : null}
              </div>
              {/* Контора открыта — ставки ещё нет, и продукт обязан спросить,
                  а не решить за человека. Ответ «поставил» и есть единственный
                  источник счёта дня и замера темпа. */}
              {(!pilotUser || (going && !failed)) && (
              <div className="nx-det__act">
                {going && !failed ? (
                  <div className="nx-det__said">
                    <button
                      type="button"
                      className="nx-det__go"
                      onClick={() => take((i + 1) as 1 | 2 | 3)}
                    >
                      поставил {amount}
                    </button>
                    <button
                      type="button"
                      className="nx-det__miss"
                      onClick={() => failLeg(row.id, (i + 1) as 1 | 2 | 3, leg.source)}
                    >
                      не вышло
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    // Не `disabled`, а приглушённая. Порядок ног — совет, а не
                    // запрет: вилочник сам решает, где очередь длиннее и что
                    // открывать первым, а запертая кнопка ему просто мешает
                    // (08.08, владелец). Совет остаётся словами на ноге.
                    className={`nx-det__go${done ? ' done' : ''}${ready ? '' : ' dim'}`}
                    onClick={() => {
                      if (callActions?.openLeg !== undefined) {
                        callActions.openLeg(i);
                        return;
                      }
                      // Вход один: карточка, витрина и клавиатура зовут `enter`.
                      // Свой обработчик здесь оставлял вторую ногу немой.
                      if (!act.enter(row, (i + 1) as 1 | 2 | 3, pair)) setBlocked(true);
                    }}
                  >
                    {done
                      ? last
                        ? `${legs.length > 2 ? 'все' : 'обе'} стоят, ${gain}`
                        : i === 0
                          ? 'первая стоит'
                          : 'вторая стоит'
                      : failed
                          ? `попробовать снова · ${amount}`
                          : act.searchDoor(leg.source, act.hrefOf(row.id, leg))
                            ? 'открыть и скопировать название'
                            : act.hrefOf(row.id, leg) !== null
                              ? `открыть и скопировать ${amount}`
                              : `скопировать ${amount}`}
                  </button>
                )}
              </div>
              )}
              <div className="nx-det__foot">
                {/* Отказ не прячем: по нему видно, на какой ноге рассыпалась
                    попытка, и он же объясняет, почему следующая нога заперта. */}
                {failed && !done && (
                  <span className="nx-det__missed">
                    {first
                      ? 'не поставлено. деньги не на кону, вилку можно бросить без последствий'
                      : 'не поставлено. первая нога стоит одна, план закрытия ниже'}
                  </span>
                )}
                {/* Кнопки «скопировать название матча» на каждой ноге больше нет:
                    в шапке тихая иконка, на двери — марка и большая кнопка. */}
                {note !== null && (
                  <span className={`nx-det__entry${linked(leg) ? '' : ' none'}`}>{note}</span>
                )}
                {needMarket !== null && <span className="nx-det__entry none">{needMarket}</span>}
                {i === polyAt && fill.state !== 'off' && (
                  <span className={`nx-det__fill ${fill.state}`}>
                    {fill.state === 'wait' && 'Polymarket: ждём шары на кошельке…'}
                    {fill.state === 'got' && `Polymarket: получено ${fill.shares} шаров`}
                    {fill.state === 'none' && (
                      <>
                        шаров нет — ордер не исполнен{' '}
                        <button
                          type="button"
                          className="nx-det__miss"
                          onClick={() =>
                            setExitKick((cur) => ({
                              n: cur.n + 1,
                              kind: stamps.some((at, j) => j !== polyAt && at !== null)
                                ? 'bet'
                                : 'shares',
                            }))
                          }
                        >
                          как выйти
                        </button>
                      </>
                    )}
                  </span>
                )}
                <BookOpenStatus id={row.id} leg={leg} />
                {/* Про счёт — до нажатия и своими словами. «Порезан» человек
                    отметил сам, перекос посчитан по его же подтверждениям
                    (`accounts.ts`). Совета не даём: другая нога вилки не
                    выбирается, выбирается сама вилка. */}
                {!polyquant && account !== null && <span className="nx-det__acc">{account}</span>}
              </div>
            </li>
          );
        })}
        {coverPick !== null && coverOpp !== undefined && (
          <CoverTile
            forkId={row.id}
            quote={coverPick}
            oppositeOdds={coverOpp.odds}
            pickLabel={betOn(
              legs.find((leg) => leg.side === coverPick.side) ?? coverOpp,
              row.row.fork,
            )}
            meta={meta}
          />
        )}
      </ol>
      {canCover && coverSide !== null && coverOpp !== undefined && (
        <CoverDock
          forkId={row.id}
          side={coverSide}
          open
          oppositeOdds={coverOpp.odds}
          meta={meta}
          onPick={(quote) => {
            setHandOdds(row.id, null);
            setCoverPick({ ...quote, side: quote.side ?? coverSide });
            setCoverSide(null);
          }}
        />
      )}
      {coverPick !== null && coverOpp !== undefined && (
        <CoverSum
          oppositeOdds={coverOpp.odds}
          quote={coverPick}
          onClear={() => setCoverPick(null)}
        />
      )}
      {legs.some((leg) => leg.ownOdds || leg.ownFee || leg.ownCur || leg.freebet) ||
      hasHand ? (
        <p className="nx-det__own">
          {legs.some((leg) => leg.ownOdds)
            ? 'свой кэф'
            : legs.some((leg) => leg.freebet)
              ? 'фрибет'
              : hasHand
                ? 'третья нога'
                : legs.some((leg) => leg.ownFee)
                  ? 'комиссия'
                  : 'своя валюта'}
          {' · '}
          {card.row.roiPct.toFixed(2)} % ·{' '}
          {`${card.row.profit < 0 ? '−' : '+'}${rub(Math.abs(card.row.profit))} ₽`}
        </p>
      ) : null}

      {/* Голая нога: первая ставка стоит, вторая названа неудачей. Карточка
          обещала «план закрытия ниже» — вот он. Совета «закрывай» или «держи»
          здесь нет: это ставка человека и его деньги. Наше дело — назвать три
          числа, которых в эту минуту нет больше нигде: что будет, если ничего
          не делать, при каком кэфе закрытие ещё не в убыток и во сколько
          обойдётся закрытие по нынешнему. */}
      {naked !== null && (
        <div className="nx-det__naked">
          <u>голая нога: {venueName(meta, naked.stood.source)}</u>
          <span>
            на кону {cardSay(cardMoney(naked.risk, fx, withRub))}: зайдёт, будет +
            {cardSay(cardMoney(naked.win, fx, withRub))}, не зайдёт, будет −
            {cardSay(cardMoney(naked.risk, fx, withRub))}. {nakedText(naked)}
          </span>
          <button
            type="button"
            onClick={() => {
              const index = (legs.indexOf(naked.left) + 1) as 1 | 2 | 3;
              void copy(formAmount(naked.stake, naked.left, fx, meta));
              if (linked(naked.left)) setBlocked(!openTabs([naked.left]));
              go(index);
            }}
          >
            закрыть {venueMoney(naked.stake, naked.left, fx, meta)} на {venueName(meta, naked.left.source)}
          </button>
        </div>
      )}

      {/* Одно действие на всю карточку: открыть обе конторы разом.
          Кнопки ног открывают по одной — так работает тот, кто ставит по
          очереди. Но вилку берут не по очереди, а параллельно: обе формы
          заполняют заранее и нажимают почти одновременно, потому что между
          первой и второй ногой живёт весь риск этого продукта.
          Раньше это умела галочка «открывать обе нажатием первой» в полосе
          инструментов — настройка вместо кнопки. Настройку надо найти, понять и
          включить, а нужна она в первую же секунду (08.08, владелец: «где
          кнопка открыть все сразу»). */}
      {/* В попапе ленты кнопки нет: конторы уже рядом. На /call — есть. */}
      {PRIVATE_MIRROR && !alone && <CupisWorkTools id={row.id} />}
      {legs.length > 1 && (!alone || callActions?.openBoth !== undefined) && (
        pilotUser ? (
          <div
            className="nx-openbar"
            style={{
              gridTemplateColumns: `repeat(${legs.length === 2 ? 3 : legs.length + 1}, minmax(0, 1fr))`,
            }}
          >
            {(legs.length === 2 ? [0, -1, 1] : [...legs.map((_, i) => i), -1]).map((slot) =>
              slot < 0 ? (
                <button
                  key="both"
                  type="button"
                  className="nx-openbar__mid"
                  title="две вкладки: матч если есть, иначе главная конторы"
                  onClick={() => {
                    if (callActions?.openBoth !== undefined) {
                      callActions.openBoth();
                      return;
                    }
                    if (legs.some((one) => act.searchDoor(one.source, act.hrefOf(row.id, one)))) {
                      void copy(act.matchQuery(row));
                    }
                    setBlocked(!openTabs(legs));
                    legs.forEach((one, i) => {
                      if (act.hrefOf(row.id, one) !== null && steps.opened[i] === null) {
                        go((i + 1) as 1 | 2 | 3);
                      }
                    });
                  }}
                >
                  открыть {legs.length > 2 ? 'все' : 'обе'}
                </button>
              ) : (
                <button
                  key={slot}
                  type="button"
                  className="nx-openbar__side"
                  onClick={() => {
                    if (callActions?.openLeg !== undefined) {
                      callActions.openLeg(slot);
                      return;
                    }
                    if (!act.enter(row, (slot + 1) as 1 | 2 | 3, pair)) setBlocked(true);
                  }}
                >
                  открыть {venueName(meta, legs[slot]!.source).toLowerCase()}
                </button>
              ),
            )}
          </div>
        ) : (
        <button
          type="button"
          className="nx-det__both"
          data-always=""
          title="две вкладки: матч если есть, иначе главная конторы"
          onClick={() => {
            if (callActions?.openBoth !== undefined) {
              callActions.openBoth();
              return;
            }
            if (legs.some((one) => act.searchDoor(one.source, act.hrefOf(row.id, one)))) {
              void copy(act.matchQuery(row));
            }
            setBlocked(!openTabs(legs));
            legs.forEach((one, i) => {
              if (act.hrefOf(row.id, one) !== null && steps.opened[i] === null) {
                go((i + 1) as 1 | 2 | 3);
              }
            });
          }}
        >
          открыть {legs.length > 2 ? 'все конторы' : 'обе конторы'} сразу
        </button>
        )
      )}
      {blocked && (
        <div className="nx-det__blocked" role="status">
          не все ссылки удалось открыть. проверьте сообщение у каждой БК и повторите открытие.
        </div>
      )}

      {/* Проверка и математика сразу под «открыть обе». Галочку «открывать обе»
          сняли: кнопка уже есть. В отдельном окне этой полосы нет — там сверка
          живёт в строке состояния внизу. */}
      {!alone && (
      <div className="nx-det__tools">
        {/* «Отзыв» — скрыт для пилотов (10.26, Рафик). */}
        {!pilotUser && <ReviewShare row={card} meta={meta} />}
        {!pilotUser && <CaptureButton row={card} meta={meta} />}
        {!pilotUser && (
        <button
          type="button"
          className="nx-det__verify"
          onClick={callActions?.verify ?? verify}
        >
          {callActions?.verify !== undefined ? 'проверить актуальность вилки' : 'проверить вилку'}
        </button>
        )}
        {/* Арифметика — по запросу, а не всем подряд (07.08, владелец):
            раскрытая, она перетягивала глаз с единственного действия карточки —
            кнопок ставок. */}
        {!pilotUser && (
        <button
          type="button"
          className={`nx-det__mathtoggle${math ? ' open' : ''}`}
          aria-expanded={math}
          onClick={() => setMath(!math)}
        >
          {math ? 'скрыть математику' : 'математика'}
        </button>
        )}
        {!alone && ((canHand && calc) || hasHand) && (
          <button type="button" className="nx-det__hand" onClick={toggleHand}>
            {hasHand ? 'убрать третью' : 'третья нога'}
          </button>
        )}
        {!alone && calc && pageQuotes.length > 0 && (
          <button type="button" className="nx-det__pageodds" onClick={applyPageOdds}>
            кэф со страницы
          </button>
        )}
        {!alone && shifted !== null && (
          <span className="nx-det__checked" role="status">
            {shifted}
          </span>
        )}
        {!alone && shifted === null && checked !== null && (
          <span
            className={`nx-det__checked ${checked.ok && checked.odds === oddsNow && now - checked.at <= CHECK_FRESH_MS ? 'ok' : 'bad'}`}
            role="status"
          >
            {checked.odds !== oddsNow ? (
              'кэф на плитке уже другой — нажмите снова, старое «жива» не про эти числа'
            ) : now - checked.at <= CHECK_FRESH_MS ? (
              <>
                {checked.text}
                <i> · проверено {Math.max(0, Math.round((now - checked.at) / 1000))} с назад</i>
              </>
            ) : (
              `проверка устарела (${Math.round((now - checked.at) / 1000)} с назад) — нажмите снова`
            )}
          </span>
        )}
      </div>
      )}

      {PAPER_UI && paper && steps.first === null && (
        <div className="nx-det__paper">
          {/* Одна строка, не абзац: режим включён, объяснение уже прочитано
              при включении. Напоминание отвечает на единственный вопрос
              момента — «это в кассу или нет». */}
          тренировка: деньги не спишутся. отметьте обе ноги, и мы скажем, успели бы вы.{' '}
          <button type="button" onClick={() => usePaper.getState().setOn(false)}>
            выключить
          </button>
        </div>
      )}
      {/* Разбор стоит в карточке, а не только в подвале: человек смотрит сюда,
          пока ждёт вторую ногу, и именно здесь узнаёт исход своей попытки. */}
      {PAPER_UI && outcome !== null && outcome !== 'open' && (
        <div className={`nx-det__paper out ${outcome}`}>
          {outcome === 'made' && (
            <>
              вилка дожила до вашего темпа. <b>деньгами это было бы {gain}</b>,
              но исполнение мы не обещаем: контора могла порезать лимит или снять кэф.
            </>
          )}
          {outcome === 'missed' && (
            <>
              вилка ушла раньше, чем вы дошли бы до второй ноги. деньгами у вас на руках
              осталась бы <b>одиночная ставка</b>. ради этого бумага и придумана.
            </>
          )}
          {outcome === 'unknown' && 'ваш темп ещё не замерен, сравнивать не с чем.'}
        </div>
      )}

      {steps.first !== null && steps.second === null && (
        <Rescue row={card} meta={meta} vow={steps.vow} />
      )}
      <ExitPane
        forkId={card.id}
        legs={legs}
        meta={meta}
        fx={fx}
        got={fill.state === 'got' ? fill.shares : null}
        kick={exitKick}
      />

      {/* Кнопка раскрытия ушла в общую полосу инструментов выше. Сама
          арифметика осталась внизу: её читают последней. */}
      {math && (
      <div className="nx-det__why">
        <p className="nx-det__math">
          вложено <b>{cardSay(staked)}</b> <s>→</s> вернётся{' '}
          <b>{cardSay(payout)}</b> при любом исходе <s>→</s> останется{' '}
          <b>{gain}</b>
        </p>
        <p className="nx-det__facts">
          {/* «Процент» сам по себе не отвечает, от чего он: на банк, на оборот
              или на вложенное. Отвечает — от вложенного, и это то же число,
              которым мерят вилку между собой. */}
          <span className={lie ? 'lie' : ''}>
            процент от вложенного <b>{card.row.roiPct.toFixed(2)} %</b>
          </span>
          {/* Глубина — только когда она измерена. У биржевой ноги зеркала
              стакана нет вовсе, и «в стакане —» читалось бы как измеренная
              пустота: не «мы не знаем», а «влезает ноль». Незнание про глубину
              называет общий блок пробелов, а не строка фактов. */}
          {legs
            .filter((leg) => leg.unit === 'share' && leg.cap.kind === 'book' && leg.cap.value !== null)
            .map((leg) => (
              <span key={leg.source}>
                в стакане <b>{leg.cap.value === null ? '—' : `${rub(leg.cap.value)} $`}</b>
              </span>
            ))}
          {/* Возраст — единственное число этого блока, которое никуда не
              переносят и ни с чем не сверяют посимвольно: это грубая
              длительность, а не сумма для чужого поля. Поэтому он без
              моноширинного `b`, как и в строке ленты. Заодно уходит спор фразы
              с самой собой: «живёт 9 с» было жирным моноширинным, а «такие
              живут 22 с» рядом — обычным, хотя это одна величина (08.08). */}
          <span>
            живёт {row.age} с{row.lifespan !== null && `, такие живут ${row.lifespan} с`}
          </span>
          {lie && (
            <span className="lie">
              процент большой, а денег мало: <b>{row.row.cappedBy ?? 'узкий объём'}</b>
            </span>
          )}
          {row.row.fork.vol !== undefined && row.row.fork.vol.length > 0 && (
            <span>
              при банке <b>{row.row.fork.vol.map((v) => `${v.bank}: ${v.roi} %`).join(' · ')}</b>
            </span>
          )}
        </p>
      </div>
      )}

      {/* Списка пройденных проверок здесь больше нет. Он назывался «честностью»,
          а работал наоборот: на боевой ленте `checks.ok` — это `fresh`, `delay`
          и `consensus`, то есть одни и те же три строки под каждой вилкой без
          исключения. Четыре галочки, стоящие всегда, не отличают хорошую строку
          от плохой и ничего не добавляют к решению — они только удлиняют окно
          (11.08, владелец: «всё, что нагрузку делает, — избавляемся»).
          Непроверенное отсюда ушло раньше и тоже снято: см. блок сомнений выше,
          там же сказано, почему. */}
      {alone && (
        <Alive
          meta={meta}
          stuck={stuck}
          onCheck={callActions?.verify ?? verify}
          checked={checked}
          shifted={shifted}
          callMode={callActions !== undefined}
          callStatus={callActions?.status ?? null}
        />
      )}
      </div>
      {(calcBar ?? alone) && (
        <div className="nx-one__bar">
          <Calc row={row} meta={meta} face="bar" stale={dead} quietHint />
        </div>
      )}

    </div>
  );
}

/**
 * Сколько лента может молчать, прежде чем это перестанет быть спокойным рынком.
 * Сердцебиение сокета сюда не считается: оно доказывает связь, а не движение
 * цен. На зеркальной ленте в полтысячи вилок минута без единой дельты означает
 * застрявший движок, а не тишину — и тогда числа на экране уже история.
 */
const QUIET_MS = 60_000;

/**
 * Жива ли вилка — последней строкой окна.
 *
 * Была строка состояния из трёх частей: точка с вердиктом, подпись «сверить
 * сейчас» и вторая строка с двумя возрастами. Владелец 08.08: «это можно
 * заменить просто на кнопку „проверить актуальность вилки“». Он прав: строка
 * отвечала на вопрос, которого никто не задавал, ровно до тех пор, пока всё
 * хорошо, — а спрашивают её в одном случае, перед нажатием.
 *
 * Поэтому в норме здесь одна кнопка и ничего больше. Молчание и есть «всё
 * сошлось»: постоянная зелёная надпись перестаёт читаться на третий день и
 * обесценивает настоящую тревогу.
 *
 * Тревога при этом не ждёт нажатия. Мёртвая вилка, потерянная связь, залипшая
 * цена и замолчавшая лента объявляются сами: молчащий движок при зелёном
 * статусе — самый дорогой обман, который наш экран умеет, человек ставит по
 * цене, которой нет уже минуту.
 *
 * Слова «вилка актуальна» на кнопке нет и в ответе не будет: актуальность — это
 * ответ конторы, а не наш. Мы знаем только, что видели вилку в последнем кадре,
 * и кнопка обещает ровно сверку с тем, что было при открытии карточки.
 */
function Alive({
  meta,
  stuck,
  onCheck,
  checked,
  shifted,
  callMode = false,
  callStatus = null,
}: {
  meta: Meta;
  stuck: Stillness | null;
  onCheck: () => void;
  checked: { at: number; ok: boolean; text: string } | null;
  shifted: string | null;
  callMode?: boolean;
  callStatus?: string | null;
}): React.ReactElement {
  const status = useScreen((s) => s.status);
  const frameAt = useScreen((s) => s.frameAt);
  const now = useScreen((s) => s.now);
  const quiet = frameAt !== null && now - frameAt > QUIET_MS;

  const trouble = callMode
    ? null
    : status !== 'live'
        ? { tone: 'gone', say: 'связь с движком потеряна, числа замерли' }
        : quiet
          ? {
              tone: 'gone',
              say: `лента молчит ${Math.round((now - frameAt!) / 1000)} с, числа могли устареть`,
            }
          : stuck !== null
            ? {
                tone: 'stuck',
                say: `кэф в ${venueName(meta, stuck.source)} стоит ${stillText(stuck)}`,
              }
            : null;
  // Беда важнее ответа на нажатие: если связь потеряна секунду назад, старое
  // «жива, кэфы те же» под кнопкой — прямая ложь.
  const say = callMode ? callStatus : trouble?.say ?? shifted ?? checked?.text ?? null;

  return (
    <div className={`nx-det__alive ${trouble?.tone ?? 'live'}`} role="status">
      <button type="button" className="nx-det__recheck" onClick={onCheck}>
        проверить актуальность вилки
      </button>
      {say !== null && <span>{say}</span>}
    </div>
  );
}
